import { lazy, ComponentType } from "react";

// Retry wrapper for lazy imports – retries once on chunk-load failure
function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch(
      () => new Promise<{ default: T }>((resolve, reject) => {
        setTimeout(() => factory().then(resolve, reject), 1500);
      }),
    ),
  );
}

// Public Pages
export const Index = lazyRetry(() => import("@/pages/Index"));
export const Search = lazyRetry(() => import("@/pages/Search"));
export const NotFound = lazyRetry(() => import("@/pages/NotFound"));
export const Auth = lazyRetry(() => import("@/pages/Auth"));
export const ResetPassword = lazyRetry(() => import("@/pages/ResetPassword"));
export const DealDetail = lazyRetry(() => import("@/pages/DealDetail"));
export const LoyaltyCheckin = lazyRetry(() => import("@/pages/LoyaltyCheckin"));
export const LoyaltyWallet = lazyRetry(() => import("@/pages/LoyaltyWallet"));
export const LoyaltyInfo = lazyRetry(() => import("@/pages/LoyaltyInfo"));
export const HowItWorksPage = lazyRetry(() => import("@/pages/HowItWorksPage"));
export const About = lazyRetry(() => import("@/pages/About"));
export const ForRestaurants = lazyRetry(() => import("@/pages/ForRestaurants"));
export const PrepaidDeals = lazyRetry(() => import("@/pages/PrepaidDeals"));
export const Contact = lazyRetry(() => import("@/pages/Contact"));
export const Impressum = lazyRetry(() => import("@/pages/Impressum"));
export const Privacy = lazyRetry(() => import("@/pages/Privacy"));
export const Terms = lazyRetry(() => import("@/pages/Terms"));
export const FAQ = lazyRetry(() => import("@/pages/FAQ"));
export const Cancellation = lazyRetry(() => import("@/pages/Cancellation"));
export const RateReservation = lazyRetry(() => import("@/pages/RateReservation"));
export const RestaurantMenu = lazyRetry(() => import("@/pages/RestaurantMenu"));
export const RestaurantDetail = lazyRetry(() => import("@/pages/RestaurantDetail"));

// Customer Pages
export const CustomerMessages = lazyRetry(() => import("@/pages/CustomerMessages"));
export const CustomerBookings = lazyRetry(() => import("@/pages/CustomerBookings"));
export const CustomerSavings = lazyRetry(() => import("@/pages/CustomerSavings"));
export const CustomerSettings = lazyRetry(() => import("@/pages/CustomerSettings"));
export const CustomerReviews = lazyRetry(() => import("@/pages/CustomerReviews"));
export const Restaurants = lazyRetry(() => import("@/pages/Restaurants"));
export const Invite = lazyRetry(() => import("@/pages/Invite"));
export const GroupJoin = lazyRetry(() => import("@/pages/GroupJoin"));
export const DealSwiper = lazyRetry(() => import("@/pages/DealSwiper"));
export const FoodGroups = lazyRetry(() => import("@/pages/FoodGroups"));
export const FoodBoard = lazyRetry(() => import("@/pages/FoodBoard"));
export const Social = lazyRetry(() => import("@/pages/Social"));
export const SocialProfile = lazyRetry(() => import("@/pages/SocialProfile"));
export const PostDetail = lazyRetry(() => import("@/pages/PostDetail"));
export const Explore = lazyRetry(() => import("@/pages/Explore"));
export const SeasonalDeals = lazyRetry(() => import("@/pages/SeasonalDeals"));
export const Compare = lazyRetry(() => import("@/pages/Compare"));
export const Favorites = lazyRetry(() => import("@/pages/Favorites"));
export const AchievementsPage = lazyRetry(() => import("@/pages/Achievements"));
export const GuestDashboard = lazyRetry(() => import("@/pages/GuestDashboard"));

// Dashboard Pages
export const DashboardHome = lazyRetry(() => import("@/pages/dashboard/DashboardHome"));
export const Onboarding = lazyRetry(() => import("@/pages/dashboard/Onboarding"));
export const DealsList = lazyRetry(() => import("@/pages/dashboard/DealsList"));
export const CreateDeal = lazyRetry(() => import("@/pages/dashboard/CreateDeal"));
export const QuickCreateDeal = lazyRetry(() => import("@/pages/dashboard/QuickCreateDeal"));
export const EditDeal = lazyRetry(() => import("@/pages/dashboard/EditDeal"));
export const Scanner = lazyRetry(() => import("@/pages/dashboard/Scanner"));
export const Stats = lazyRetry(() => import("@/pages/dashboard/Stats"));
export const Settings = lazyRetry(() => import("@/pages/dashboard/Settings"));
export const LoyaltyDashboard = lazyRetry(() => import("@/pages/dashboard/LoyaltyDashboard"));
export const RevenueIntelligence = lazyRetry(() => import("@/pages/dashboard/RevenueIntelligence"));
export const DashboardMessages = lazyRetry(() => import("@/pages/dashboard/DashboardMessages"));
export const CustomerGeography = lazyRetry(() => import("@/pages/dashboard/CustomerGeography"));
export const ReviewsDashboard = lazyRetry(() => import("@/pages/dashboard/ReviewsDashboard"));
export const MenuManagement = lazyRetry(() => import("@/pages/dashboard/MenuManagement"));
export const FloorPlan = lazyRetry(() => import("@/pages/dashboard/FloorPlan"));
export const PickupOrders = lazyRetry(() => import("@/pages/dashboard/PickupOrders"));
export const GuestCRMPage = lazyRetry(() => import("@/pages/dashboard/GuestCRMPage"));
export const MarketingPage = lazyRetry(() => import("@/pages/dashboard/MarketingPage"));
export const RestaurantSocial = lazyRetry(() => import("@/pages/dashboard/RestaurantSocial"));
export const FoodBoardDashboard = lazyRetry(() => import("@/pages/dashboard/FoodBoardDashboard"));
export const QuickHub = lazyRetry(() => import("@/pages/dashboard/QuickHub"));
export const ServiceTypes = lazyRetry(() => import("@/pages/dashboard/ServiceTypes"));
export const DashboardNotifications = lazyRetry(() => import("@/pages/dashboard/Notifications"));
export const BillingView = lazyRetry(() => import("@/pages/dashboard/BillingView"));
export const Transparency = lazyRetry(() => import("@/pages/dashboard/Transparency"));

// Admin Pages
export const AdminHome = lazyRetry(() => import("@/pages/admin/AdminHome"));
export const AdminActivity = lazyRetry(() => import("@/pages/admin/AdminActivity"));
export const AdminRestaurants = lazyRetry(() => import("@/pages/admin/AdminRestaurants"));
export const AdminGuests = lazyRetry(() => import("@/pages/admin/AdminGuests"));
export const AdminGuestDetail = lazyRetry(() => import("@/pages/admin/AdminGuestDetail"));
export const AdminReservations = lazyRetry(() => import("@/pages/admin/AdminReservations"));
export const AdminBilling = lazyRetry(() => import("@/pages/admin/AdminBilling"));
export const AdminMessages = lazyRetry(() => import("@/pages/admin/AdminMessages"));
export const AdminReviews = lazyRetry(() => import("@/pages/admin/AdminReviews"));
export const AdminRestaurantDetail = lazyRetry(() => import("@/pages/admin/AdminRestaurantDetail"));
export const AdminAuditLog = lazyRetry(() => import("@/pages/admin/AdminAuditLog"));
export const AdminContactMessages = lazyRetry(() => import("@/pages/admin/AdminContactMessages"));
export const AdminSocial = lazyRetry(() => import("@/pages/admin/AdminSocial"));
export const AdminAnalytics = lazyRetry(() => import("@/pages/admin/AdminAnalytics"));
export const AdminReports = lazyRetry(() => import("@/pages/admin/AdminReports"));
export const AdminRoles = lazyRetry(() => import("@/pages/admin/AdminRoles"));
export const AdminSystemHealth = lazyRetry(() => import("@/pages/admin/AdminSystemHealth"));
export const AdminSeoFindings = lazyRetry(() => import("@/pages/admin/AdminSeoFindings"));

export const TypographyAudit = lazyRetry(() => import("@/pages/dev/TypographyAudit"));
